import { ChevronRight } from 'lucide-react'
import type { NotificationItem } from '@/api/generated/models'
import { renderNotificationText, type NotificationLookup } from './notification-render'
import { formatRelative } from './format-relative'
import { useUserTimeZone } from '@/lib/useUserTimeZone'

interface NotificationRowProps {
  item: NotificationItem
  lookup: NotificationLookup
  onOpen: (item: NotificationItem) => void
}

// One notification row, shared by the /notifications page and the desktop
// dropdown. The whole row is the single click target (=> mark read + navigate
// to item.link, handled by the parent's onOpen). The rendered text is plain —
// entity names are emphasized text, NOT links — so a click anywhere on the row
// opens the notification (no competing inline link). role="button" (not a native
// <button>) keeps the flexible block layout while staying keyboard-operable.
// Unread rows are visually distinct (tinted background + a leading dot).
//
// Because the TEXT is deliberately not a link, the ROW has to carry the "this
// goes somewhere" cue itself, at rest (Russ, 2026-07-27: read rows read as dead
// prose — "very dark, don't even show they are clickable"). Three things do it:
// a pointer cursor (a role="button" div doesn't get one for free — the cursor
// stayed an arrow over every row), a trailing chevron on EVERY row so read and
// unread carry the same affordance, and a visible focus ring. Deliberately not
// recoloring the prose blue: a feed of blue sentences is louder than the cue
// needs to be, and it would imply the words are separate targets.
export function NotificationRow({ item, lookup, onOpen }: NotificationRowProps) {
  const timeZone = useUserTimeZone()
  const isUnread = !item.read

  function handleClick() {
    onOpen(item)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpen(item)
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={[
        'group flex w-full cursor-pointer items-start gap-2 px-3 py-2 text-left hover:bg-slate-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue',
        isUnread ? 'bg-blue-50/60' : '',
      ].join(' ')}
    >
      {isUnread && (
        <span
          aria-label="Unread"
          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600"
        />
      )}
      <span className="min-w-0 flex-1">
        <span className={isUnread ? 'font-medium text-navy' : 'text-slate-700'}>
          {renderNotificationText(item, lookup)}
        </span>
        <span className="mt-0.5 block text-xs text-slate-400">
          {formatRelative(item.createdOn, timeZone)}
        </span>
      </span>
      <ChevronRight
        aria-hidden="true"
        className="mt-0.5 h-4 w-4 shrink-0 self-center text-slate-300 transition-colors group-hover:text-navy"
      />
    </div>
  )
}
